import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '../../lib/utils';
import {
  LayoutDashboard,
  Package,
  Boxes,
  ShoppingCart,
  BarChart3,
  DollarSign,
  Settings,
  LogOut,
  MessageSquare,
  List,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

interface SidebarProps {
  className?: string;
  isCollapsed?: boolean;
  onToggle?: () => void;
}

const menuItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
  { icon: Package, label: 'Products', path: '/products' },
  { icon: Boxes, label: 'Bundles', path: '/bundles' },
  { icon: List, label: 'Inventory', path: '/inventory' },
  { icon: ShoppingCart, label: 'Orders', path: '/orders' },
  { icon: BarChart3, label: 'Analytics', path: '/analytics' },
  { icon: DollarSign, label: 'Earnings', path: '/earnings' },
  { icon: MessageSquare, label: 'Messages', path: '/messages' },
  { icon: Settings, label: 'Settings', path: '/settings' },
];

export function Sidebar({ className, isCollapsed, onToggle }: SidebarProps) {
  const location = useLocation();

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  };

  return (
    <div className={cn(
      'flex h-screen flex-col bg-gray-900 text-white transition-all duration-300 relative',
      isCollapsed ? 'w-20' : 'w-64',
      className
    )}>
      {/* Brand Header */}
      <div className={cn(
        'sticky top-0 z-20 bg-white flex items-center gap-3 p-5 border-b border-gray-200 transition-all duration-300',
        isCollapsed ? 'justify-center px-2' : 'px-6'
      )}>
        {isCollapsed ? (
          <img src="/assets/logo/favicon.png" className="h-8 w-8 object-contain" alt="ShoplyCircle Icon" />
        ) : (
          <div className="flex flex-col">
            <img src="/assets/logo/logo.svg" className="h-8 w-auto object-contain" alt="ShoplyCircle Logo" />
            <span className="text-[9px] uppercase tracking-widest text-emerald-600 font-extrabold mt-1 ml-0.5 leading-none">Manufacturer Hub</span>
          </div>
        )}
      </div>

      {/* Collapse Toggle (Desktop only) */}
      <button
        onClick={onToggle}
        className="absolute -right-3 top-20 hidden md:flex h-6 w-6 items-center justify-center rounded-full border border-gray-800 bg-gray-900 text-gray-400 hover:text-white transition-colors z-50"
      >
        {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
      </button>

      <nav className="flex-1 px-3 py-6 space-y-1.5 overflow-y-auto custom-scrollbar">
        {menuItems.map((item) => {
          const isActive = location.pathname.startsWith(item.path);
          return (
            <Link
              key={item.path}
              to={item.path}
              className={cn(
                'group flex items-center rounded-xl px-3 py-3 text-sm font-medium transition-all duration-200 relative',
                isActive
                  ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-500/10'
                  : 'text-gray-400 hover:bg-gray-800 hover:text-white',
                isCollapsed && 'justify-center px-0'
              )}
            >
              <item.icon className={cn(
                'h-5 w-5 transition-transform duration-200',
                isActive ? 'scale-110' : 'group-hover:scale-110',
                !isCollapsed && 'mr-3'
              )} />
              {!isCollapsed && (
                <span className="animate-in fade-in slide-in-from-left-2 duration-300">
                  {item.label}
                </span>
              )}
              {isCollapsed && isActive && (
                <div className="absolute left-0 w-1 h-6 bg-emerald-500 rounded-r-full" />
              )}
            </Link>
          );
        })}
      </nav>

      <div className="sticky bottom-0 z-20 p-4 border-t border-gray-800 bg-gray-900 backdrop-blur-md">
        <button
          onClick={handleLogout}
          className={cn(
            'flex w-full items-center rounded-xl px-3 py-3 text-sm font-medium text-gray-400 hover:bg-red-500/10 hover:text-red-500 transition-all duration-200',
            isCollapsed && 'justify-center px-0'
          )}
        >
          <LogOut className={cn('h-5 w-5', !isCollapsed && 'mr-3')} />
          {!isCollapsed && <span>Logout</span>}
        </button>
      </div>
    </div>
  );
}
